import Button from 'components/common/Button';
import { FadeIn } from 'components/common/FadeIn';
import EditableLotteryEntry from 'enter2026late/EditableLotteryEntry';
import { useEnterStore } from 'hooks/stores/useEnterStore';
import { useIsMobile } from 'hooks/ui/useIsMobile';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { formattedPriceStringFromPence } from 'utils/common';

import Envelope from '../../svg/Envelope.svg';
import PlusRing from '../../svg/PlusRing.svg';
import { useLotteryEntryPickerSlidingModal } from '../LotteryEntryPickerSlidingModal';
import EntryWrapper, { EntryBreaker, EntryContent } from '../OtherEntry';
import PostalEntrySlidingModal from '../PostalEntrySlidingModal';
import TitleSection from '../TitleSection';
import styles from './V1EntriesPicker.module.css';

const V1EntriesPicker = observer(() => {
  const [postalModalOpen, setPostalModalOpen] = useState(false);
  const enterStore = useEnterStore();
  const { isMobile } = useIsMobile();
  const slidingEntryPicker = useLotteryEntryPickerSlidingModal({
    slideAmount: 'static',
  });

  const addEntryLines = (entryNum: number) => {
    enterStore.setNumEntries(entryNum);
  };

  const allEntryCounts = Object.entries(
    enterStore.plan?.priceTable[0].GBP || [],
  );
  const singleEntryCost = allEntryCounts[0] ? allEntryCounts[0][1] : 0;
  const nextEntryCostInPence =
    (enterStore.lines.length === 1
      ? allEntryCounts[1]?.[1]
      : allEntryCounts[2]?.[1]) ?? singleEntryCost;

  return (
    <div>
      <TitleSection
        title={
          <>
            Pick your <br />
            numbers
          </>
        }
        subtitle="The more you match, the bigger the prize"
        displayMascot={isMobile}
        showInfoGraphic
      />
      <div className={styles.entryContainer}>
        {enterStore.lines.map((x, i) => (
          <FadeIn onScroll duration={0.3} key={i}>
            <EditableLotteryEntry
              main={x.main}
              bonus={x.bonus}
              lineNumber={i + 1}
              showTapToChange={i === 0}
              onRemoveClick={i > 0 ? () => enterStore.removeLine(i) : undefined}
              onRandomiseClick={() => enterStore.generateLineRandomNumbers(i)}
              onEditClick={() => {
                slidingEntryPicker.startEditing(i);
              }}
              pulse
            />
          </FadeIn>
        ))}
        <FadeIn onScroll duration={0.3}>
          <EntryWrapper
            hideEntry={enterStore.lines.length > 2}
            onClick={() => addEntryLines(enterStore.lines.length + 1)}
            badge={enterStore.lines.length === 1 ? 'Best Value' : 'Popular'}
            badgeColor={enterStore.lines.length === 1 ? 'orange' : 'teal'}
          >
            <EntryContent
              icon={<PlusRing />}
              textTitle={`Add ${enterStore.lines.length === 1 ? '2nd' : '3rd'} entry & save`}
              textSubtitle={
                enterStore.lines.length === 1
                  ? 'Double your chances'
                  : 'Maximise your chances'
              }
              textOrange={`+ ${formattedPriceStringFromPence(nextEntryCostInPence)}/week - Save ${singleEntryCost - nextEntryCostInPence}p`}
            />
          </EntryWrapper>
        </FadeIn>
        <FadeIn onScroll duration={0.3}>
          <EntryBreaker />
        </FadeIn>
        <FadeIn onScroll duration={0.3} delay={0.05}>
          <EntryWrapper onClick={() => setPostalModalOpen(true)}>
            <EntryContent
              icon={<Envelope />}
              textTitle="Postal entry"
              textSubtitle="No purchase necessary"
              rightContent={
                <Button
                  buttonStyle="Tertiary"
                  buttonSize="Small"
                  type={undefined}
                >
                  Read More
                </Button>
              }
            />
          </EntryWrapper>
        </FadeIn>
      </div>
      <PostalEntrySlidingModal
        isOpen={postalModalOpen}
        onClose={() => setPostalModalOpen(false)}
      />
      {slidingEntryPicker.render()}
    </div>
  );
});

export default V1EntriesPicker;
